// src/hooks/useSuppliers.ts - Hooks pour la gestion des fournisseurs

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { Supplier, SupplierFilters, PaymentTerms } from '../types/suppliers';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { matchesSearchInFields } from '../utils/searchUtils';

// ===== HOOKS POUR LES FOURNISSEURS =====

export const useSuppliers = (filters?: SupplierFilters) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['suppliers', businessId, filters],
    queryFn: async () => {
      if (!businessId) {
        console.warn('Aucun businessId disponible');
        return [];
      }

      try {
        let q = query(
          collection(db, COLLECTIONS.suppliers),
          where('businessId', '==', businessId),
          orderBy('name', 'asc')
        );

        // Note: Le filtre isActive a été retiré - tous les fournisseurs sont actifs

        // Filtre par conditions de paiement
        if (filters?.paymentTerms) {
          q = query(q, where('paymentTerms', '==', filters.paymentTerms));
        }

        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useSuppliers - Main query');
          throw error;
        });
        
        let suppliers = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            businessId: data.businessId,
            name: data.name,
            contactPerson: data.contactPerson,
            phone: data.phone,
            email: data.email,
            address: data.address,
            paymentTerms: data.paymentTerms,
            notes: data.notes,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
          } as Supplier;
        });

        // Filtre côté client pour la recherche
        if (filters?.searchTerm) {
          suppliers = suppliers.filter(supplier =>
            matchesSearchInFields(
              [supplier.name, supplier.contactPerson, supplier.phone, supplier.email],
              filters.searchTerm!
            )
          );
        }

        return suppliers;
        
      } catch (error) {
        console.error('Erreur lors de la récupération des fournisseurs:', error);
        return [];
      }
    },
    enabled: !!businessId && (permissions?.canViewSuppliers ?? false),
  });
};

export const useSupplier = (supplierId: string) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['suppliers', businessId, supplierId],
    queryFn: async () => {
      if (!supplierId || !businessId) return null;

      const docRef = doc(db, COLLECTIONS.suppliers, supplierId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Fournisseur introuvable');
      }

      const data = docSnap.data();
      
      // Vérifier l'appartenance au business
      if (data.businessId !== businessId) {
        throw new Error('Ce fournisseur n\'appartient pas à votre business');
      }

      return {
        id: docSnap.id,
        businessId: data.businessId,
        name: data.name,
        contactPerson: data.contactPerson,
        phone: data.phone,
        email: data.email,
        address: data.address,
        paymentTerms: data.paymentTerms,
        notes: data.notes,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as Supplier;
    },
    enabled: !!supplierId && !!businessId && (permissions?.canViewSuppliers ?? false),
  });
};

export const useAddSupplier = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
  const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (supplierData: Omit<Supplier, 'id' | 'businessId' | 'createdAt' | 'updatedAt'>) => {
      if (!businessId || !userProfile) {
        throw new Error('Business ou utilisateur non défini');
      }

      // Vérifier l'unicité du nom de fournisseur
      const existingQuery = query(
        collection(db, COLLECTIONS.suppliers),
        where('businessId', '==', businessId),
        where('name', '==', supplierData.name.trim())
      );
      
      const existingSnap = await getDocs(existingQuery).catch(error => {
        handleFirestoreError(error, 'useSuppliers - Existing query');
        throw error;
      });
      if (!existingSnap.empty) {
        throw new Error('Un fournisseur avec ce nom existe déjà');
      }

      const newSupplier: any = {
        businessId,
        name: supplierData.name.trim(),
        phone: supplierData.phone.trim(),
        paymentTerms: supplierData.paymentTerms,
        createdAt: Timestamp.now(),
      };
      
      // Ajouter conditionnellement les champs optionnels
      if (supplierData.contactPerson?.trim()) {
        newSupplier.contactPerson = supplierData.contactPerson.trim();
      }
      
      if (supplierData.email?.trim()) {
        newSupplier.email = supplierData.email.trim();
      }
      
      if (supplierData.address?.trim()) {
        newSupplier.address = supplierData.address.trim();
      }
      
      if (supplierData.notes?.trim()) {
        newSupplier.notes = supplierData.notes.trim();
      }

      const docRef = await addDoc(collection(db, COLLECTIONS.suppliers), newSupplier);
      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers', businessId] });
    },
  });
};

export const useUpdateSupplier = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Supplier> & { id: string }) => {
      if (!businessId) {
        throw new Error('Business non défini');
      }

      // Vérifier que le fournisseur appartient au business
      const supplierRef = doc(db, COLLECTIONS.suppliers, id);
      const supplierSnap = await getDoc(supplierRef);
      
      if (!supplierSnap.exists()) {
        throw new Error('Fournisseur introuvable');
      }
      
      const existingSupplier = supplierSnap.data();
      if (existingSupplier.businessId !== businessId) {
        throw new Error('Ce fournisseur n\'appartient pas à votre business');
      }

      const updateData: any = {
        updatedAt: Timestamp.now(),
      };

      // Vérifier l'unicité du nom si il change
      if (data.name && data.name !== existingSupplier.name) {
        const existingQuery = query(
          collection(db, COLLECTIONS.suppliers),
          where('businessId', '==', businessId),
          where('name', '==', data.name.trim())
        );
        
        const existingSnap = await getDocs(existingQuery).catch(error => {
        handleFirestoreError(error, 'useSuppliers - Existing query');
        throw error;
      });
        const duplicateExists = existingSnap.docs.some(doc => doc.id !== id);
        
        if (duplicateExists) {
          throw new Error('Un fournisseur avec ce nom existe déjà');
        }
        
        updateData.name = data.name.trim();
      }

      // Autres champs modifiables
      if (data.contactPerson !== undefined) {
        updateData.contactPerson = data.contactPerson?.trim() || null;
      }
      if (data.phone) updateData.phone = data.phone.trim();
      if (data.email !== undefined) {
        updateData.email = data.email?.trim() || null;
      }
      if (data.address !== undefined) {
        updateData.address = data.address?.trim() || null;
      }
      if (data.paymentTerms) updateData.paymentTerms = data.paymentTerms;
      if (data.notes !== undefined) {
        updateData.notes = data.notes?.trim() || null;
      }

      await updateDoc(supplierRef, updateData);
      return { id, ...data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers', businessId] });
    },
  });
};

export const useDeleteSupplier = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (supplierId: string) => {
      if (!businessId) {
        throw new Error('Business non défini');
      }

      // Vérifier qu'aucune livraison n'utilise ce fournisseur
      const deliveriesQuery = query(
        collection(db, COLLECTIONS.deliveries),
        where('businessId', '==', businessId),
        where('supplierId', '==', supplierId)
      );
      
      const deliveriesSnap = await getDocs(deliveriesQuery).catch(error => {
        handleFirestoreError(error, 'useSuppliers - Deliveries query');
        throw error;
      });
      if (!deliveriesSnap.empty) {
        throw new Error(
          `Impossible de supprimer ce fournisseur car ${deliveriesSnap.size} livraison(s) l'utilisent`
        );
      }

      // Vérifier qu'aucun produit n'a ce fournisseur comme fournisseur préféré
      const productsQuery = query(
        collection(db, COLLECTIONS.products),
        where('businessId', '==', businessId),
        where('preferredSupplierId', '==', supplierId)
      );
      
      const productsSnap = await getDocs(productsQuery).catch(error => {
        handleFirestoreError(error, 'useSuppliers - Products query');
        throw error;
      });
      if (!productsSnap.empty) {
        throw new Error(
          `Impossible de supprimer ce fournisseur car ${productsSnap.size} produit(s) l'utilisent comme fournisseur préféré`
        );
      }

      // Supprimer le fournisseur
      const supplierRef = doc(db, COLLECTIONS.suppliers, supplierId);
      await deleteDoc(supplierRef);

      return supplierId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers', businessId] });
    },
  });
};

// Hook pour les statistiques des fournisseurs
export const useSuppliersStats = () => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['suppliers', 'stats', businessId],
    queryFn: async () => {
      if (!businessId) {
        return {
          totalSuppliers: 0,
          activeSuppliers: 0,
          totalDeliveries: 0,
          totalAmount: 0,
          averageDeliveryValue: 0,
        };
      }

      try {
        // Compter les fournisseurs
        const suppliersQuery = query(
          collection(db, COLLECTIONS.suppliers),
          where('businessId', '==', businessId)
        );
        const suppliersSnap = await getDocs(suppliersQuery).catch(error => {
          handleFirestoreError(error, 'useSuppliers - Suppliers query');
          throw error;
        });
        
        const totalSuppliers = suppliersSnap.size;
        const activeSuppliers = suppliersSnap.docs.filter(
          doc => doc.data().isActive === true
        ).length;

        // Compter les livraisons
        const deliveriesQuery = query(
          collection(db, COLLECTIONS.deliveries),
          where('businessId', '==', businessId)
        );
        const deliveriesSnap = await getDocs(deliveriesQuery).catch(error => {
        handleFirestoreError(error, 'useSuppliers - Deliveries query');
        throw error;
      });
        
        let totalAmount = 0;
        deliveriesSnap.docs.forEach(doc => {
          const data = doc.data();
          totalAmount += data.totalAmount || 0;
        });

        const totalDeliveries = deliveriesSnap.size;
        const averageDeliveryValue = totalDeliveries > 0 ? totalAmount / totalDeliveries : 0;

        return {
          totalSuppliers,
          activeSuppliers,
          totalDeliveries,
          totalAmount,
          averageDeliveryValue,
        };
        
      } catch (error) {
        console.error('Erreur lors du calcul des statistiques fournisseurs:', error);
        return {
          totalSuppliers: 0,
          activeSuppliers: 0,
          totalDeliveries: 0,
          totalAmount: 0,
          averageDeliveryValue: 0,
        };
      }
    },
    enabled: !!businessId && (permissions?.canViewSuppliers ?? false),
  });
};